/**
 * Hook for loading and managing the AI-generated filter profile
 * This profile is created after quiz completion and used for "For You" filtering
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/services';
import { cacheConfig, getCachedData, setCacheData, invalidateCache } from '@/lib/config/cacheConfig';
import type { CatalogFiltersState } from './useCatalogFilters';
import type { Json } from '@/integrations/supabase/types';

export interface AIFilterProfile {
  id: string;
  name: string;
  filters: Partial<CatalogFiltersState>;
  ai_label: string | null;
  updated_at: string;
}

/**
 * Safely parse filters from JSONB
 */
function parseFilters(filtersJson: Json): Partial<CatalogFiltersState> {
  if (!filtersJson || typeof filtersJson !== 'object' || Array.isArray(filtersJson)) {
    return {};
  }
  
  const f = filtersJson as Record<string, unknown>;
  
  return {
    speciesKey: typeof f.speciesKey === 'string' ? f.speciesKey : null,
    breedIds: Array.isArray(f.breedIds) ? f.breedIds.filter((id): id is string => typeof id === 'string') : [],
    breedNames: typeof f.breedNames === 'object' && f.breedNames !== null ? f.breedNames as Record<string, string> : {},
    sex: Array.isArray(f.sex) ? f.sex.filter((s): s is string => typeof s === 'string') : [],
    ageBuckets: Array.isArray(f.ageBuckets) ? f.ageBuckets.filter((b): b is string => typeof b === 'string') : [],
    geoPlaceId: typeof f.geoPlaceId === 'string' ? f.geoPlaceId : null,
    geoPlaceName: typeof f.geoPlaceName === 'string' ? f.geoPlaceName : null,
    sizes: Array.isArray(f.sizes) ? f.sizes.filter((s): s is string => typeof s === 'string') : [],
    colors: Array.isArray(f.colors) ? f.colors.filter((c): c is string => typeof c === 'string') : [],
    healthSpecialNeeds: typeof f.healthSpecialNeeds === 'boolean' ? f.healthSpecialNeeds : null,
    onlyWithPhotos: typeof f.onlyWithPhotos === 'boolean' ? f.onlyWithPhotos : false,
  };
}

export function useAIFilterProfile() {
  const { user } = useAuth();
  const [aiProfile, setAiProfile] = useState<AIFilterProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAIProfile = useCallback(async (forceRefresh = false) => {
    if (!user) {
      setAiProfile(null);
      setIsLoading(false);
      return null;
    }

    const { ttlMs, storageKey } = cacheConfig.aiFilterProfile;

    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = getCachedData<AIFilterProfile & { _cachedUserId: string }>(storageKey, ttlMs);
      if (cached && cached._cachedUserId === user.id) {
        const { _cachedUserId, ...profile } = cached;
        setAiProfile(profile);
        setIsLoading(false);
        return profile;
      }
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_filter_profiles')
        .select('id, name, filters, ai_label, updated_at')
        .eq('user_id', user.id)
        .eq('is_ai', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        const profile: AIFilterProfile = {
          id: data.id,
          name: data.name || 'Подбор по анкете',
          filters: parseFilters(data.filters),
          ai_label: data.ai_label,
          updated_at: data.updated_at,
        };
        // Cache with userId marker
        setCacheData(storageKey, { ...profile, _cachedUserId: user.id });
        setAiProfile(profile);
        return profile;
      }
      
      setAiProfile(null);
      return null;
    } catch (err) {
      logger.error('Failed to fetch AI profile', {
        context: { feature: 'catalog', function_name: 'fetchAIProfile' },
        error: err as Error,
        error_code: 'fetch_ai_profile_failed',
        error_type: 'unexpected',
      });
      setAiProfile(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const refresh = useCallback(async () => {
    invalidateCache(cacheConfig.aiFilterProfile.storageKey);
    return fetchAIProfile(true);
  }, [fetchAIProfile]);

  useEffect(() => {
    fetchAIProfile();
  }, [fetchAIProfile]);

  return {
    aiProfile,
    isLoading,
    hasAIProfile: !!aiProfile,
    refresh,
  };
}
